import * as Sentry from '@sentry/browser';

export interface HttpError {
    status: number;
    responseText: string;
    statusText: string;
    responseJSON: {
        errorMessage: string;
    };
}

export class Logger {
    private readonly isSentryEnabled: boolean;

    constructor(isSentryEnabled: boolean = false, sentryOptions: object = {}) {
        this.isSentryEnabled = isSentryEnabled;
        if (this.isSentryEnabled) {
            Sentry.init(sentryOptions);
        }
    }

    sendToSentry(error: Error, context = {}) {
        if (!this.isSentryEnabled) {
            return;
        }

        if (!(error instanceof Error)) {
            // Add the "error" as extra context and create a real Error object for Sentry.
            error = new Error('Unknown client error.');
        }
        const scope = new Sentry.Scope();
        scope.setContext('app_error_context', context);
        Sentry.captureException(error, scope);
    }

    info(message: string, context = {}) {
        console.info(message, context);
    }

    debug(message: string, context = {}) {
        console.debug(message, context);
    }

    warning(message: string, context = {}) {
        console.warn(message, context);
    }

    error(error: Error, context = {}) {
        this.sendToSentry(error, context);
        Object.keys(context).length > 0 ? console.error(error, context) : console.error(error);
    }

    httpError(error: HttpError | Error, context = {}) {
        if ('status' in error && error?.status < 500) {
            return;
        }
        if (!(error instanceof Error)) {
            // Add the "error" as extra context and create a real Error object for Sentry.
            error = new Error(
                error?.responseJSON?.errorMessage || error?.responseText || error.statusText || 'Unknown',
            );
            // @ts-expect-error Context is intentionally untyped.
            context._originalError = error;
        }

        this.sendToSentry(error, context);
    }
}
